import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HelpTipSourceContext } from '@/shared/ui';

import { type HelpLanguage, helpLanguageOf, loadHelpContent } from './content-loader';
import type { HelpContent, HelpTopicId } from './topics';

/**
 * Gives every contextual "?" the short text of its Help Center article, in the interface language.
 * The guide text is not part of the main bundle: nothing is downloaded until a tip that needs it
 * is first shown (`request`), and the download is shared with the Help Center itself.
 */
export function HelpTipProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const language = helpLanguageOf(i18n.language);
  const [wanted, setWanted] = useState(false);
  const [loaded, setLoaded] = useState<{ language: HelpLanguage; content: HelpContent } | null>(
    null,
  );

  useEffect(() => {
    if (!wanted) return;
    let current = true;
    loadHelpContent(language).then(
      (content) => {
        if (current) setLoaded({ language, content });
      },
      () => undefined, // a failed download only leaves the tip without text; the next show retries
    );
    return () => {
      current = false;
    };
  }, [wanted, language]);

  const request = useCallback(() => {
    setWanted(true);
  }, []);
  const value = useMemo(
    () => ({
      request,
      tip: (id: HelpTopicId) => {
        if (loaded?.language !== language) return undefined;
        const topic = loaded.content[id];
        return topic.tip ?? topic.lead;
      },
    }),
    [request, loaded, language],
  );
  return <HelpTipSourceContext.Provider value={value}>{children}</HelpTipSourceContext.Provider>;
}
